import { useState } from "react"
import { ArrowCounterClockwise } from "@phosphor-icons/react"
import { toast } from "react-toastify"

import { Button } from "~/components/design-system/components"
import { ConfirmationDialog } from "~/components/reusable/ConfirmationDialog"
import { useSaveMetadata } from "~/hooks/useMetadata"
import { api } from "~/utils/api"

type Props = {
  variantIds: string[]
  disabled?: boolean
  disabledTooltip?: string
  onSuccess?: () => void
}

const variantWord = (count: number) => (count === 1 ? "variant" : "variants")

export function ReactivateLifecycleDialog({
  variantIds,
  disabled,
  disabledTooltip,
  onSuccess,
}: Props) {
  const [open, setOpen] = useState(false)
  const saveMetadata = useSaveMetadata({ showToast: false })
  const count = variantIds.length

  const { data: skuIds } = api.sku.getSkuIdsForVariants.useQuery(
    { variantIds },
    { enabled: count > 0 },
  )

  const handleConfirm = () => {
    if (!skuIds || skuIds.length === 0) return
    saveMetadata.mutate(
      { skuIds, lifecycleStatus: "active", phasingOutFrom: null, activatedUntil: null },
      {
        onSuccess: () => {
          toast.success(`Reactivated ${count} ${variantWord(count)}`)
          setOpen(false)
          onSuccess?.()
        },
      },
    )
  }

  return (
    <ConfirmationDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && saveMetadata.isPending) return
        setOpen(next)
      }}
      title={`Reactivate ${count} ${variantWord(count)}`}
      description="Reactivated variants move back to Active or the Inbox, depending on whether they're in a collection."
      confirmText={saveMetadata.isPending ? "Reactivating..." : `Reactivate ${variantWord(count)}`}
      onConfirm={handleConfirm}
      loading={saveMetadata.isPending}
      requires="edit"
      disabled={disabled}
      disabledTooltip={disabledTooltip}
      trigger={
        <Button size="sm" variant="neutral">
          <ArrowCounterClockwise size={16} />
          Reactivate
        </Button>
      }
    />
  )
}
